// =============================================================================
// composables/useModalA11y.js — accesibilidad de modales (RX-3 · hallazgo R3)
// -----------------------------------------------------------------------------
// Al ABRIR el modal: guarda el elemento enfocado, enfoca el primer control y
// activa un manejador de teclado global que:
//   - atrapa el foco (Tab/Shift+Tab dentro del modal),
//   - cierra con Escape (llamando a onClose).
// Al CERRAR: restaura el foco al elemento previo.
//
// Uso en un <script setup>:
//   const modalRoot = ref(null);
//   useModalA11y(modalRoot, () => props.show, () => emit('close'));
// Y en la plantilla: <div ref="modalRoot" v-if="show" ...>
// =============================================================================
import { watch, onBeforeUnmount } from 'vue';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export function useModalA11y(rootRef, isOpen, onClose) {
  let previouslyFocused = null;
  let keydownHandler = null;

  const getFocusable = () => {
    const root = rootRef.value;
    if (!root) return [];
    return Array.from(root.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter((el) => el.offsetParent !== null || el === document.activeElement);
  };

  const trapFocus = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable();
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === rootRef.value || !rootRef.value.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const handleKeydown = (event) => {
    if (event.key === 'Escape' && typeof onClose === 'function') {
      event.stopPropagation();
      onClose();
    } else if (event.key === 'Tab') {
      trapFocus(event);
    }
  };

  const activate = () => {
    if (keydownHandler) return;
    keydownHandler = handleKeydown;
    document.addEventListener('keydown', keydownHandler);
  };

  const deactivate = () => {
    if (!keydownHandler) return;
    document.removeEventListener('keydown', keydownHandler);
    keydownHandler = null;
  };

  watch(isOpen, (open) => {
    if (open) {
      previouslyFocused = document.activeElement;
      activate();
      // El primer elemento enfocable del modal recibe el foco (o el propio contenedor).
      const focusable = getFocusable();
      const target = focusable[0] || rootRef.value;
      target?.focus();
    } else {
      deactivate();
      previouslyFocused?.focus?.();
      previouslyFocused = null;
    }
  }, { immediate: true });

  onBeforeUnmount(deactivate);
}